import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { facePlane } from '../../core/sketch/types';
import { planeBasis } from '../../core/sketch/triangulate';
import type { InferenceResult } from '../../core/sketch/inference';
import { useSketchToolEnv } from './sketchInput';
import { registerSketchTool, type SketchHit } from './sketchToolBus';
import { useEscapeToSelect } from './SketchCameraTools';

/**
 * UCS (drawing-plane) tools — AutoCAD-style one-click alignment of the
 * Sketch drawing axes, complementing the three-click Axes tool.
 *
 * Every tool writes the same useSketchStore.drawingAxes the Axes tool and
 * the sketch input plumbing already consume: the sticky drawing plane for
 * empty-space strokes starts from it (sketchInput.ts) and the red/green/blue
 * AxesIndicator renders it, so a UCS change immediately steers where new
 * geometry lands.
 *
 * - World UCS: one-shot reset to the Z=0 world ground.
 * - Face UCS: pick a face — the axes land on its plane at the picked point
 *   (stays armed for repeated alignments, like the Axes tool).
 * - View UCS: one-shot — the axes turn screen-parallel through the orbit
 *   target, so strokes follow the current view direction.
 */

interface OrbitControlsLike {
    target: THREE.Vector3;
}

const backToSelect = (): void => {
    useAppStore.getState().setActiveTool('select');
};

const isFaceHit = (hit: SketchHit): hit is SketchHit & { solidId: string; faceId: string } =>
    hit.kind === 'face' && !!hit.solidId && !!hit.faceId;

// ============================== WORLD UCS ==============================

/** World UCS: one-shot restore of the world Z=0 ground (drawingAxes null). */
export function useSketchUcsWorldTool(): void {
    useEscapeToSelect();
    useEffect(() => {
        useSketchStore.getState().setDrawingAxes(null, 'World UCS');
        backToSelect();
    }, []);
}

// ============================== FACE UCS ==============================

/** Face UCS: hover inference marker; a face pick re-bases the drawing
 *  axes onto that face's plane (origin at the picked point). The tool
 *  stays armed for repeated alignments; Escape hands back to Select. */
export function useSketchUcsFaceTool() {
    const env = useSketchToolEnv();
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                if (!isFaceHit(hit)) return;
                const solid = useSketchStore.getState().solids[hit.solidId!];
                const face = solid?.faces.find((f) => f.id === hit.faceId);
                if (!face) return;
                const origin = env.infer(hit, [], null).point.clone();
                const { u, v } = planeBasis(facePlane(face).normal);
                useSketchStore.getState().setDrawingAxes({
                    origin: [origin.x, origin.y, origin.z],
                    xAxis: [u.x, u.y, u.z],
                    yAxis: [v.x, v.y, v.z],
                }, 'Face UCS');
            },
        });
        return () => registerSketchTool(null);
    }, [env]);

    useEscapeToSelect();

    return { cursor };
}

// ============================== VIEW UCS ==============================

/** View UCS: one-shot — the drawing axes turn parallel to the screen
 *  (X = camera right, Y = camera up) through the orbit target, so strokes
 *  land on a plane facing the viewer at the looked-at depth. */
export function useSketchUcsViewTool(): void {
    useEscapeToSelect();
    const camera = useThree((s) => s.camera);
    const controls = useThree((s) => s.controls) as OrbitControlsLike | null;

    useEffect(() => {
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
        const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
        if (right.lengthSq() < 1e-12 || up.lengthSq() < 1e-12) return;
        const origin = controls ? controls.target.clone() : new THREE.Vector3();
        useSketchStore.getState().setDrawingAxes({
            origin: [origin.x, origin.y, origin.z],
            xAxis: [right.x, right.y, right.z],
            yAxis: [up.x, up.y, up.z],
        }, 'View UCS');
        backToSelect();
    }, [camera, controls]);
}
